"use client";

import { useState, useTransition } from "react";
import { deleteLead, updateLead } from "../actions";
import type { LeadRecord } from "../types";
import { BTN_GHOST, BTN_PRIMARY, ConfirmButton, Notice, PageTitle } from "./ui";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "replied", label: "Replied" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
] as const;

const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Messages from the website's contact form, newest first. */
export function LeadsInbox({ leads: initial }: { leads: LeadRecord[] }) {
  const [leads, setLeads] = useState(initial);
  const [filter, setFilter] = useState<(typeof STATUSES)[number]["value"]>("new");
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [pending, startTransition] = useTransition();

  const setStatus = (id: string, status: LeadRecord["status"]) =>
    startTransition(async () => {
      const result = await updateLead(id, status);
      if (result.ok) setLeads((list) => list.map((l) => (l.id === id ? { ...l, status } : l)));
      setNotice(result.ok ? { type: "ok", text: status === "replied" ? "Marked as replied." : status === "archived" ? "Archived." : "Moved back to New." } : { type: "error", text: result.error });
    });
  const remove = (id: string) =>
    startTransition(async () => {
      const result = await deleteLead(id);
      if (result.ok) setLeads((list) => list.filter((l) => l.id !== id));
      setNotice(result.ok ? { type: "ok", text: "Deleted." } : { type: "error", text: result.error });
    });

  const shown = leads.filter((l) => filter === "all" || l.status === filter);
  const count = (status: string) => (status === "all" ? leads.length : leads.filter((l) => l.status === status).length);

  return (
    <div aria-busy={pending}>
      <PageTitle title="Leads" />
      <p className="-mt-3 mb-5 text-midnight/70">Messages sent through the website&apos;s contact form. Reply by email, then mark them as replied.</p>
      <div role="tablist" aria-label="Show" className="mb-4 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            role="tab"
            aria-selected={filter === s.value}
            onClick={() => setFilter(s.value)}
            className={`${BTN_GHOST} ${filter === s.value ? "border-midnight bg-midnight text-offwhite hover:text-offwhite" : ""}`}
          >
            {s.label} ({count(s.value)})
          </button>
        ))}
      </div>
      <Notice notice={notice} />
      {shown.length ? (
        <ul className="mt-4 flex flex-col gap-3">
          {shown.map((l) => (
            <li key={l.id} className={`rounded-2xl border p-4 sm:p-5 ${l.status === "new" ? "border-amber bg-amber/5" : "border-midnight/10 bg-sand"}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 dir="auto" className="text-lg font-bold">{l.name}</h2>
                <span className="font-mono text-xs text-midnight/70">{when(l.createdAt)}</span>
              </div>
              <a href={`mailto:${l.email}`} className="inline-block py-1 break-all text-teal underline underline-offset-4">
                {l.email}
              </a>
              <p className="mt-1 text-sm">
                <strong>Budget:</strong> <bdi>{l.budget}</bdi>
                {l.lang === "ar" && (
                  <>
                    {" · "}
                    <strong>Wrote in Arabic</strong> (reply in Arabic)
                  </>
                )}
              </p>
              {l.summary && (
                <p dir="auto" className="mt-3 leading-relaxed whitespace-pre-line">
                  {l.summary}
                </p>
              )}
              <p className="mt-3 font-mono text-xs text-midnight/70">
                Sent from {l.page} · via {l.source} · {l.device} · {l.region}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`mailto:${l.email}?subject=${encodeURIComponent("Your message to TASMA")}`} className={BTN_PRIMARY}>
                  Reply by email
                </a>
                {l.status !== "replied" && (
                  <button type="button" onClick={() => setStatus(l.id, "replied")} className={BTN_GHOST}>
                    Mark replied
                  </button>
                )}
                {l.status !== "archived" && (
                  <button type="button" onClick={() => setStatus(l.id, "archived")} className={BTN_GHOST}>
                    Archive
                  </button>
                )}
                {l.status !== "new" && (
                  <button type="button" onClick={() => setStatus(l.id, "new")} className={BTN_GHOST}>
                    Mark new
                  </button>
                )}
                <ConfirmButton question="Delete this message?" onConfirm={() => remove(l.id)} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-2xl border border-dashed border-midnight/20 p-8 text-center text-midnight/70">
          {leads.length ? "Nothing here." : "No messages yet. They'll appear here when someone uses the contact form."}
        </p>
      )}
    </div>
  );
}
